import apiClient, { ApiResponse, PaginatedResponse } from '../api/client';
import { PaymentConfiguration, ModuleTemplate, BillingCycle } from '../types/models';

export interface CreatePaymentConfigurationData {
  payment_method: string;
  provider_name: string;
  api_key?: string;
  api_secret?: string;
  merchant_id?: string;
  callback_url?: string;
  is_active?: boolean;
  configuration?: Record<string, any>;
}

export interface CreateModuleTemplateData {
  module_type_id: number;
  name: string;
  description?: string;
  configuration: {
    requires_approval?: boolean;
    auto_generate_reference?: boolean;
    default_billing_cycle?: string;
    features?: string[];
    settings?: Record<string, any>;
  };
}

export interface CreateBillingCycleData {
  name: string;
  code: string;
  days: number;
  description?: string;
}

class ConfigurationService {
  private paymentConfigBaseUrl = '/admin/county/payment-configurations';
  private moduleTemplateBaseUrl = '/admin/county/module-templates';
  private billingCycleBaseUrl = '/admin/county/billing-cycles';

  // Payment Configuration endpoints
  async getPaymentConfigurations(page?: number): Promise<ApiResponse<PaginatedResponse<PaymentConfiguration>>> {
    const params = page ? `?page=${page}` : '';
    return apiClient.get<PaginatedResponse<PaymentConfiguration>>(`${this.paymentConfigBaseUrl}${params}`);
  }

  async getPaymentConfiguration(id: number): Promise<ApiResponse<PaymentConfiguration>> {
    return apiClient.get<PaymentConfiguration>(`${this.paymentConfigBaseUrl}/${id}`);
  }

  async createPaymentConfiguration(data: CreatePaymentConfigurationData): Promise<ApiResponse<PaymentConfiguration>> {
    return apiClient.post<PaymentConfiguration>(this.paymentConfigBaseUrl, data);
  }

  async updatePaymentConfiguration(id: number, data: Partial<CreatePaymentConfigurationData>): Promise<ApiResponse<PaymentConfiguration>> {
    return apiClient.put<PaymentConfiguration>(`${this.paymentConfigBaseUrl}/${id}`, data);
  }

  async deletePaymentConfiguration(id: number): Promise<ApiResponse> {
    return apiClient.delete(`${this.paymentConfigBaseUrl}/${id}`);
  }

  async togglePaymentConfigurationStatus(id: number): Promise<ApiResponse<PaymentConfiguration>> {
    return apiClient.post<PaymentConfiguration>(`${this.paymentConfigBaseUrl}/${id}/toggle-status`);
  }

  async getActivePaymentMethods(): Promise<ApiResponse<PaymentConfiguration[]>> {
    return apiClient.get<PaymentConfiguration[]>(`${this.paymentConfigBaseUrl}/active`);
  }

  // Module Template endpoints
  async getModuleTemplates(page?: number): Promise<ApiResponse<PaginatedResponse<ModuleTemplate>>> {
    const params = page ? `?page=${page}` : '';
    return apiClient.get<PaginatedResponse<ModuleTemplate>>(`${this.moduleTemplateBaseUrl}${params}`);
  }

  async getModuleTemplate(id: number): Promise<ApiResponse<ModuleTemplate>> {
    return apiClient.get<ModuleTemplate>(`${this.moduleTemplateBaseUrl}/${id}`);
  }

  async createModuleTemplate(data: CreateModuleTemplateData): Promise<ApiResponse<ModuleTemplate>> {
    return apiClient.post<ModuleTemplate>(this.moduleTemplateBaseUrl, data);
  }

  async updateModuleTemplate(id: number, data: Partial<CreateModuleTemplateData>): Promise<ApiResponse<ModuleTemplate>> {
    return apiClient.put<ModuleTemplate>(`${this.moduleTemplateBaseUrl}/${id}`, data);
  }

  async deleteModuleTemplate(id: number): Promise<ApiResponse> {
    return apiClient.delete(`${this.moduleTemplateBaseUrl}/${id}`);
  }

  async getTemplatesByType(typeId: number): Promise<ApiResponse<ModuleTemplate[]>> {
    return apiClient.get<ModuleTemplate[]>(`${this.moduleTemplateBaseUrl}/type/${typeId}`);
  }

  // Billing Cycle endpoints
  async getBillingCycles(page?: number): Promise<ApiResponse<PaginatedResponse<BillingCycle>>> {
    const params = page ? `?page=${page}` : '';
    return apiClient.get<PaginatedResponse<BillingCycle>>(`${this.billingCycleBaseUrl}${params}`);
  }

  async getBillingCycle(id: number): Promise<ApiResponse<BillingCycle>> {
    return apiClient.get<BillingCycle>(`${this.billingCycleBaseUrl}/${id}`);
  }

  async createBillingCycle(data: CreateBillingCycleData): Promise<ApiResponse<BillingCycle>> {
    return apiClient.post<BillingCycle>(this.billingCycleBaseUrl, data);
  }

  async updateBillingCycle(id: number, data: Partial<CreateBillingCycleData>): Promise<ApiResponse<BillingCycle>> {
    return apiClient.put<BillingCycle>(`${this.billingCycleBaseUrl}/${id}`, data);
  }

  async deleteBillingCycle(id: number): Promise<ApiResponse> {
    return apiClient.delete(`${this.billingCycleBaseUrl}/${id}`);
  }
}

export const configurationService = new ConfigurationService();
export default configurationService;
